import { useState, useEffect, useRef } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { api } from "../../../lib/api";

const KIND_BADGE = {
    subject: "bg-blue-100 text-blue-800",
    grade: "bg-purple-100 text-purple-800",
    board: "bg-amber-100 text-amber-800",
    country: "bg-emerald-100 text-emerald-800",
    state: "bg-rose-100 text-rose-800",
    city: "bg-orange-100 text-orange-800",
    pincode: "bg-slate-100 text-slate-700",
    language: "bg-cyan-100 text-cyan-800",
    exam_category: "bg-pink-100 text-pink-800",
    curriculum_type: "bg-indigo-100 text-indigo-800",
    curriculum_pack: "bg-violet-100 text-violet-800",
};

export default function GlobalSearch({ onJump }) {
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const timer = useRef(null);

    useEffect(() => {
        if (!q.trim()) { setItems([]); return; }
        clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            setLoading(true);
            try {
                const r = await api.get(`/admin/master-ops/search?q=${encodeURIComponent(q.trim())}&limit=20`);
                setItems(r.data.items || []);
                setOpen(true);
            } finally { setLoading(false); }
        }, 250);
        return () => clearTimeout(timer.current);
    }, [q]);

    return (
        <div className="relative" data-testid="md-global-search">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onFocus={() => items.length && setOpen(true)}
                    placeholder="Search any country, curriculum, board, grade, subject…"
                    data-testid="md-global-search-input"
                    className="w-full pl-9 pr-9 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                {q && (
                    <button onClick={() => { setQ(""); setItems([]); setOpen(false); }} data-testid="md-global-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100">
                        <X className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                )}
            </div>

            {open && (q.trim() || loading) && (
                <div className="absolute z-30 mt-1 left-0 right-0 bg-white rounded-xl border border-slate-200 shadow-xl max-h-80 overflow-y-auto" data-testid="md-global-search-results">
                    {loading && <div className="px-3 py-3 text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</div>}
                    {!loading && items.length === 0 && <div className="px-3 py-3 text-xs text-slate-500">No matches.</div>}
                    {!loading && items.map((it) => (
                        <button
                            key={it.id}
                            onClick={() => { onJump?.(it.kind); setOpen(false); }}
                            data-testid={`md-gs-result-${it.id}`}
                            className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-slate-50 text-sm border-b border-slate-100 last:border-b-0"
                        >
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${KIND_BADGE[it.kind] || "bg-slate-100 text-slate-700"}`}>{it.kind}</span>
                            <span className="font-semibold text-slate-800 truncate">{it.value}</span>
                            {it.code && <span className="text-[10px] text-slate-500">({it.code})</span>}
                            {!it.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 ml-auto">inactive</span>}
                            {it.is_draft && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">draft</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
